import React, { useEffect, useState } from "react";
import { RefreshCw, Download, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useI18n } from "@/i18n/I18nContext";
import api, { API } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const STATUSES = ["pending_payment", "paid", "generating_lyrics", "generating_music", "generating_thumbnail", "completed", "failed", "refunded"];

const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "-");
const fmtValue = (v) => {
  if (v === true) return "✓";
  if (v === false || v === null || v === undefined || v === "") return "-";
  return String(v);
};

export default function AdminOrdersTab() {
  const { t } = useI18n();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [selectedOrder, setSelectedOrder] = useState(null);

  const load = () => api.get("/admin/orders", { params: { search: search || undefined, status: status === "all" ? undefined : status } }).then(({ data }) => setOrders(data));

  useEffect(() => { load(); }, [search, status]);

  const retry = async (id) => {
    await api.post(`/admin/orders/${id}/retry`);
    load();
  };

  const refundTokens = async (id) => {
    await api.post(`/admin/orders/${id}/refund-tokens`);
    load();
  };

  const exportCsv = () => {
    const token = localStorage.getItem("ams_token");
    window.open(`${API}/admin/export/csv?type=orders&auth=${token}`, "_blank");
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input data-testid={ADMIN.searchInput} placeholder={t("admin.searchPlaceholder")} value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs rounded-full" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid={ADMIN.statusFilter} className="w-48 rounded-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("admin.filterAll")}</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" className="ml-auto rounded-full" data-testid={ADMIN.exportCsvBtn("orders")} onClick={exportCsv}>
          <Download className="mr-2 h-4 w-4" />
          {t("admin.exportCsv")}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="p-4">ID</th>
              <th className="p-4">Total</th>
              <th className="p-4">Statut</th>
              <th className="p-4">Date</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.order_id} data-testid={ADMIN.orderRow(o.order_id)} className="border-t border-border">
                <td className="p-4 font-mono text-xs">{o.order_id}</td>
                <td className="p-4 font-semibold">{o.pricing.total}€</td>
                <td className="p-4"><Badge variant="outline" className="rounded-full">{o.status}</Badge></td>
                <td className="p-4 text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</td>
                <td className="p-4">
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      data-testid={ADMIN.viewOrderDetailsBtn(o.order_id)}
                      onClick={() => setSelectedOrder(o)}
                    >
                      <Eye className="mr-1 h-3.5 w-3.5" />
                      {t("admin.viewDetails")}
                    </Button>
                    {o.status === "failed" && (
                      <>
                        <Button size="sm" variant="outline" className="rounded-full" data-testid={ADMIN.retryOrderBtn(o.order_id)} onClick={() => retry(o.order_id)}>
                          <RefreshCw className="mr-1 h-3.5 w-3.5" />
                          {t("admin.retryGeneration")}
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full" data-testid={ADMIN.refundBtn(o.order_id)} onClick={() => refundTokens(o.order_id)}>
                          {t("admin.refundTokens")}
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selectedOrder} onOpenChange={(open) => !open && setSelectedOrder(null)}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid={ADMIN.orderDetailsDialog}>
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono text-sm">{selectedOrder.order_id}</DialogTitle>
              </DialogHeader>

              <div className="space-y-5 text-sm">
                <div className="flex items-center justify-between rounded-xl bg-muted/40 p-3">
                  <div>
                    <p className="font-medium">{selectedOrder.customer_name || "-"}</p>
                    <p className="text-xs text-muted-foreground">{selectedOrder.customer_email || "-"}</p>
                  </div>
                  <Badge variant="outline" className="rounded-full">{selectedOrder.status}</Badge>
                </div>

                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("admin.orderDates")}</h4>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><p className="text-muted-foreground">{t("admin.orderCreatedAt")}</p><p>{fmtDate(selectedOrder.created_at)}</p></div>
                    <div><p className="text-muted-foreground">{t("admin.orderPaidAt")}</p><p>{fmtDate(selectedOrder.paid_at)}</p></div>
                    <div><p className="text-muted-foreground">{t("admin.orderCompletedAt")}</p><p>{fmtDate(selectedOrder.completed_at)}</p></div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("admin.orderOptions")}</h4>
                  <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 rounded-xl border border-border p-3">
                    {Object.entries(selectedOrder.form || {}).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-muted-foreground">{key}</span>
                        <span className="max-w-[55%] truncate text-right font-medium" title={fmtValue(value)}>{fmtValue(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("admin.orderPricingBreakdown")}</h4>
                  <div className="rounded-xl border border-border p-3">
                    {Object.entries(selectedOrder.pricing || {}).filter(([k]) => !["currency"].includes(k)).map(([key, value]) => (
                      <div key={key} className={`flex items-center justify-between py-0.5 text-xs ${key === "total" ? "mt-1 border-t border-border pt-1.5 font-semibold" : "text-muted-foreground"}`}>
                        <span>{key}</span>
                        <span>{typeof value === "number" ? `${value}€` : fmtValue(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {selectedOrder.generated?.title && (
                  <div>
                    <h4 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">{t("admin.orderGenerated")}</h4>
                    <div className="rounded-xl border border-border p-3 text-xs space-y-1">
                      <p><span className="text-muted-foreground">Titre: </span>{selectedOrder.generated.title}</p>
                      <p><span className="text-muted-foreground">Versions: </span>{selectedOrder.generated.versions?.length || 0}</p>
                      <p><span className="text-muted-foreground">Miniature: </span>{(selectedOrder.generated.thumbnail_storage_path || selectedOrder.generated.gallery_thumbnail_storage_path) ? t("admin.yes") : t("admin.no")}</p>
                    </div>
                  </div>
                )}

                {selectedOrder.error_message && (
                  <div>
                    <h4 className="mb-2 text-xs font-semibold uppercase text-destructive">{t("admin.orderErrorMessage")}</h4>
                    <p className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">{selectedOrder.error_message}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
